import React from "react";
import "@/App.css";
import { BrowserRouter, Navigate, Routes, Route } from "react-router-dom";
import { AppProvider, useApp } from "@/context/AppContext";
import { Toaster } from "sonner";
import AppLayout from "@/components/layout/AppLayout";
import Overview from "@/pages/Overview";
import Shops from "@/pages/Shops";
import ShopDetail from "@/pages/ShopDetail";
import Subscriptions from "@/pages/Subscriptions";
import Payments from "@/pages/Payments";
import Referrals from "@/pages/Referrals";
import SalesPipeline from "@/pages/SalesPipeline";
import CreateSalesAgent from "@/pages/CreateSalesAgent";
import SupportTickets from "@/pages/SupportTickets";
import SyncHealth from "@/pages/SyncHealth";
import Analytics from "@/pages/Analytics";
import AdminUsers from "@/pages/AdminUsers";
import AuditLogs from "@/pages/AuditLogs";
import Settings from "@/pages/Settings";
import AcceptInvitation from "@/pages/AcceptInvitation";
import ForgotPassword from "@/pages/ForgotPassword";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: true,
      retry: 2,
      staleTime: 30_000,
    },
  },
});

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AppProvider>
        <BrowserRouter>
          <Routes>
            <Route path="/accept-invitation" element={<AcceptInvitation />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/login" element={<Navigate to="/" replace />} />
            <Route element={<AppLayout />}>
              <Route path="/" element={<PermissionPage permission="overview.view"><Overview /></PermissionPage>} />
              <Route path="/shops" element={<PermissionPage permission="shops.view"><Shops /></PermissionPage>} />
              <Route path="/shops/:id" element={<PermissionPage permission="shops.view"><ShopDetail /></PermissionPage>} />
              <Route path="/subscriptions" element={<PermissionPage permission="subscriptions.view"><Subscriptions /></PermissionPage>} />
              <Route path="/payments" element={<PermissionPage permission="payments.view"><Payments /></PermissionPage>} />
              <Route path="/referrals" element={<PermissionPage permission="referrals.view"><Referrals /></PermissionPage>} />
              <Route path="/sales" element={<PermissionPage permission="sales.view"><SalesPipeline /></PermissionPage>} />
              <Route path="/sales/agents/new" element={<PermissionPage permission="sales.manage"><CreateSalesAgent /></PermissionPage>} />
              <Route path="/support" element={<PermissionPage permission="support.view"><SupportTickets /></PermissionPage>} />
              <Route path="/sync" element={<PermissionPage permission="sync.view"><SyncHealth /></PermissionPage>} />
              <Route path="/analytics" element={<PermissionPage permission="analytics.view"><Analytics /></PermissionPage>} />
              <Route path="/admins" element={<PermissionPage permission="admin_users.view"><AdminUsers /></PermissionPage>} />
              <Route path="/audit" element={<PermissionPage permission="audit.view"><AuditLogs /></PermissionPage>} />
              <Route path="/settings" element={<PermissionPage permission="settings.manage"><Settings /></PermissionPage>} />
            </Route>
          </Routes>
        </BrowserRouter>
        <Toaster
          position="top-right"
          toastOptions={{
            style: {
              background: "#fff",
              border: "1px solid rgba(14,91,82,0.1)",
              color: "#064E3B",
              borderRadius: "12px",
            },
          }}
        />
      </AppProvider>
    </QueryClientProvider>
  );
}

function PermissionPage({ children, permission }) {
  const { currentAdmin } = useApp();
  if (!currentAdmin?.permissions?.includes(permission)) return <Navigate to="/" replace />;
  return children;
}

export default App;
